import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api';
import { ENDPOINTS } from '../constants/endpoints';
import { SelectOption } from '../interfaces/select-option';
import { BulkImportNotAllowedError, UserLimitError } from '../lib/tier-errors';

interface CreateUserData {
  email?: string;
  phone?: string;
  name: string;
  surname: string;
  roleId: number;
  organisationId: number;
  departments: string[];
}

interface UpdateUserData {
  userId: number;
  organisationId: number;
  name: string;
  surname: string;
  email?: string;
  phone?: string;
  roleId: number;
  status: string;
  departments: string[];
}

interface BulkImportUser {
  email?: string;
  phone?: string;
  name: string;
  surname: string;
  roleId: number;
  organisationId: number;
  departments: string[];
}

interface BulkImportData {
  users: BulkImportUser[];
}

interface BulkImportResult {
  successful: { identifier: string }[];
  failed: { identifier: string; error: string }[];
}

interface UserData {
  id: number;
  name: string;
  surname: string;
  email: string;
  phone: string;
  status: string;
  role_id: number;
  roles: {
    id: number;
    name: string;
  };
  user_departments: {
    departments: { id: number; name: string };
  }[];
}

interface GetUserResponse {
  user: UserData;
  userDepartments: string[];
  availableDepartments: SelectOption[];
}

interface PaginatedUsers {
  users: UserData[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface PermanentlyDeleteUserData {
  userId: number;
  organisationId: number;
}

async function createUser(data: CreateUserData) {
  const response = await apiFetch(ENDPOINTS.USER_CREATE, {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (errorData.code === UserLimitError.CODE) {
      throw new UserLimitError(errorData.message);
    }
    throw new Error(errorData.message || 'Failed to create user');
  }

  return response.json();
}

async function updateUser(data: UpdateUserData) {
  const response = await apiFetch(ENDPOINTS.USER_UPDATE, {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to update user');
  }

  return response.json();
}

async function bulkImportUsers(
  data: BulkImportData,
): Promise<BulkImportResult> {
  const response = await apiFetch(ENDPOINTS.USER_BULK_IMPORT, {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (errorData.code === BulkImportNotAllowedError.CODE) {
      throw new BulkImportNotAllowedError(errorData.message);
    }
    if (errorData.code === UserLimitError.CODE) {
      throw new UserLimitError(errorData.message);
    }
    throw new Error(errorData.message || 'Failed to import users');
  }

  return response.json();
}

async function fetchUsers(
  organisationId: number,
  page: number,
  limit: number,
  role?: string,
  status?: string,
  search?: string,
  departmentIds?: number[],
): Promise<PaginatedUsers> {
  const response = await apiFetch(
    ENDPOINTS.USER_LIST(
      organisationId,
      page,
      limit,
      role,
      status,
      search,
      departmentIds,
    ),
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch users');
  }

  return response.json();
}

async function fetchUser(
  userId: number,
  organisationId: number,
): Promise<GetUserResponse> {
  const response = await apiFetch(ENDPOINTS.USER_GET(userId, organisationId));

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to fetch user');
  }

  return response.json();
}

async function permanentlyDeleteUser(params: PermanentlyDeleteUserData) {
  const response = await apiFetch(
    ENDPOINTS.USER_PERMANENTLY_DELETE(params.userId, params.organisationId),
    { method: 'DELETE' },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to delete user');
  }

  return response.json();
}

export function usePermanentlyDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: permanentlyDeleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });
}

export function useBulkImportUsers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bulkImportUsers,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });
}

export function useUsers(
  organisationId: number | undefined,
  page: number,
  limit: number,
  role?: string,
  status?: string,
  search?: string,
  departmentIds?: number[],
) {
  return useQuery({
    queryKey: [
      'user',
      'list',
      organisationId,
      page,
      limit,
      role,
      status,
      search,
      departmentIds,
    ],
    queryFn: () =>
      fetchUsers(
        organisationId!,
        page,
        limit,
        role,
        status,
        search,
        departmentIds,
      ),
    enabled: !!organisationId,
  });
}

export function useUser(
  userId: number | undefined,
  organisationId: number | undefined,
) {
  return useQuery({
    queryKey: ['user', userId, organisationId],
    queryFn: () => fetchUser(userId!, organisationId!),
    enabled: !!userId && !!organisationId,
  });
}
